import { supabase } from '../client/supabaseClient';

interface WhatsAppDocument {
  session_id: string;
  phone_number: string;
  document_count: number;
  document_urls: string[];
  file_names: string[]; // Array of original file names
  created_at: string;
}

/**
 * Fetches documents from a WhatsApp session
 * @param sessionId - The WhatsApp session ID
 * @param phoneNumber - Optional phone number to further filter results
 * @returns WhatsApp session document information
 */
export async function fetchWhatsAppDocuments(
  sessionId: string,
  phoneNumber?: string
): Promise<WhatsAppDocument | null> {
  try {
    // TODO: Implement proper rpc calls 
    const { data, error } = await supabase    .from('whatsapp_sessions')
      .select('id, phone_number, document_count, document_urls, file_names, created_at')
      .eq('id', sessionId)
      .single();

    console.log(data, error);

    if (error) {
      console.error('Error fetching WhatsApp documents:', error);
      throw error;
    }

    if (!data) {
      console.log(`No WhatsApp session found with ID: ${sessionId}`);
      return null;
    }    // Use the direct query result
      // Parse document_urls which might be a JSON string of a string array
    let parsedUrls: string[] = [];
    
    try {
      if (typeof data.document_urls === 'string') {
        // Handle case where it's a JSON string like "[\"url1\", \"url2\"]"
        const parsed = JSON.parse(data.document_urls);
        if (Array.isArray(parsed)) {
          parsedUrls = parsed;
        }
      } else if (Array.isArray(data.document_urls)) {
        // Handle case where it's already an array
        parsedUrls = data.document_urls;
      } else if (data.document_urls && typeof data.document_urls === 'object') {
        // Handle case where it's an object, extract values
        parsedUrls = Object.values(data.document_urls);
      }
      
      // Clean up URLs - remove any trailing question marks
      parsedUrls = parsedUrls.map(url => url.endsWith('?') ? url.slice(0, -1) : url);
      
    } catch (parseError) {
      console.error('Error parsing document_urls:', parseError);
      parsedUrls = [];
    }
      // Parse file_names the same way as document_urls
    let parsedFileNames: string[] = [];
    try {
      if (typeof data.file_names === 'string') {
        const parsed = JSON.parse(data.file_names);
        if (Array.isArray(parsed)) {
          parsedFileNames = parsed;
        }
      } else if (Array.isArray(data.file_names)) {
        parsedFileNames = data.file_names;
      } else if (data.file_names && typeof data.file_names === 'object') {
        parsedFileNames = Object.values(data.file_names);
      }
    } catch (parseError) {
      console.error('Error parsing file_names:', parseError);
      parsedFileNames = [];
    }

    // Return the formatted data
    return {
      session_id: data.id,
      phone_number: data.phone_number || '',
      document_count: data.document_count || (parsedUrls.length || 0),
      document_urls: parsedUrls,
      file_names: parsedFileNames,
      created_at: data.created_at
    };
  } catch (error) {
    console.error('Failed to fetch WhatsApp documents:', error);
    throw error;
  }
}
